/**
 * /admin/v1/runtime 与 /admin/v1/write-actions API 客户端。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export type AgentQualityMode = "q_fast_grounding_side_effect" | "q_deep_side_effect";
export type AgentWriteMode = "disabled" | "dry_run" | "live_confirmed";
export type WriteActionStatus =
	| "pending_confirmation"
	| "live_executed"
	| "live_failed"
	| "rejected"
	| "expired";

export interface AgentRuntimeConfig {
	merchant_id: string;
	sdk_loop_enabled: boolean;
	max_steps: number;
	quality_mode: AgentQualityMode;
	write_mode: AgentWriteMode;
	live_write_requires_confirmation: boolean;
	live_write_requires_q_deep: boolean;
	live_write_mutation_direct_from_agent: boolean;
	updated_by: string | null;
	notes: string | null;
	updated_at: string | null;
}

export interface AgentRuntimeConfigPutRequest {
	sdk_loop_enabled: boolean;
	max_steps: number;
	quality_mode: AgentQualityMode;
	write_mode: AgentWriteMode;
	updated_by?: string | null;
	notes?: string | null;
}

export interface WriteActionItem {
	audit_id: string;
	merchant_id: string;
	session_id: string | null;
	trace_id: string | null;
	customer_id: string | null;
	skill_id: string;
	tool_id: string;
	idempotency_key: string;
	order_id: string | null;
	status: WriteActionStatus;
	write_mode: string;
	confirmation_status: string;
	confirmation_plan: Record<string, unknown>;
	dry_run_action: Record<string, unknown>;
	q_gate: Record<string, unknown>;
	live_result: Record<string, unknown> | null;
	mutation_executed: boolean;
	confirmed_by: string | null;
	confirmed_at: string | null;
	rejected_by: string | null;
	rejected_at: string | null;
	reject_reason: string | null;
	created_at: string | null;
	updated_at: string | null;
}

export interface WriteActionListResponse {
	items: WriteActionItem[];
	total: number;
}

export function useAgentRuntimeConfig(merchantId: string) {
	return useQuery<AgentRuntimeConfig>({
		queryKey: ["admin", "runtime", "agent", merchantId],
		queryFn: async () => {
			const { data } = await apiClient.get<AgentRuntimeConfig>(
				`/admin/v1/runtime/merchants/${encodeURIComponent(merchantId)}/agent`,
			);
			return data;
		},
		enabled: merchantId.length > 0,
	});
}

export function usePutAgentRuntimeConfig(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<AgentRuntimeConfig, unknown, AgentRuntimeConfigPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<AgentRuntimeConfig>(
				`/admin/v1/runtime/merchants/${encodeURIComponent(merchantId)}/agent`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "runtime", "agent", merchantId] });
		},
	});
}

export function useWriteActions(merchantId: string, status?: WriteActionStatus) {
	return useQuery<WriteActionListResponse>({
		queryKey: ["admin", "write-actions", merchantId, status ?? "all"],
		queryFn: async () => {
			const { data } = await apiClient.get<WriteActionListResponse>("/admin/v1/write-actions", {
				params: { merchant_id: merchantId, status },
			});
			return data;
		},
		enabled: merchantId.length > 0,
	});
}

export function useConfirmWriteAction(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<
		WriteActionItem,
		unknown,
		{ auditId: string; idempotencyKey: string; confirmedBy?: string }
	>({
		mutationFn: async ({ auditId, idempotencyKey, confirmedBy }) => {
			const { data } = await apiClient.post<WriteActionItem>(
				`/admin/v1/write-actions/${encodeURIComponent(auditId)}/confirm`,
				{ confirmed_by: confirmedBy?.trim() || "admin-console", idempotency_key: idempotencyKey },
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "write-actions", merchantId] });
		},
	});
}

export function useRejectWriteAction(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<
		WriteActionItem,
		unknown,
		{ auditId: string; rejectedBy?: string; reason?: string }
	>({
		mutationFn: async ({ auditId, rejectedBy, reason }) => {
			const { data } = await apiClient.post<WriteActionItem>(
				`/admin/v1/write-actions/${encodeURIComponent(auditId)}/reject`,
				{
					rejected_by: rejectedBy?.trim() || "admin-console",
					reason: reason?.trim() || "rejected from Admin Console",
				},
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "write-actions", merchantId] });
		},
	});
}
